import millify from "millify";
import ReactCountryFlag from "react-country-flag";
import UnlockDataBanner from "components/shareable/UnlockDataBanner";
import UnlockDataImage from "assets/images/unlock-data-geographies.svg";
import Loader from "elements/Loader";
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { GraphTooltip } from "pages/DashboardPage/components/Graphs";
import { AxisInterval } from "recharts/types/util/types";
import { fixCountryNameForUnited, performanceTextForDemos, performanceTextForLocations } from "./helpers";
import { BarChartOutlined } from "@material-ui/icons";
import getSymbolFromCurrency from "currency-symbol-map";
import { CurrencyCodesType } from "modules/Types";
import { DEFAULT_CURRENCY, PlaybookType, PlanType } from "modules/Const";
import useBrand from 'Hooks/useBrand';
import useScreen from "Hooks/useScreen";
import ButtonSymphony from 'elements/ButtonSymphony';
import Colors from "modules/Colors";
import CardSymphony from "elements/CardSymphony";
import Sty from "modules/Sty";
import { CampaignDetailsData, GeographicBreakdownItem, DemographicsBreakdownItem } from "api/campaignApi";
import BlurredGraph from "elements/BlurredGraph";
import useCampaignDetails from "Hooks/useCampaignDetails";
import { useFeature } from 'Hooks/useFeature';
import { FeatureKeys } from 'features/definitions';
import { CSSProperties, useState } from "react";
import { capitalizeFirstLetter, clsx, styx, formatNumberToDisplayType } from "modules/Utils";
import InputSymphony, { SelectOptionItem } from "elements/InputSymphony";

/** Facebook action type interface for extracting engagement metrics */
interface FacebookActionType {
    action_type: string;
    value: string | number;
}

/**
 * Helper function to extract shares from Facebook actions array
 * @param actions - Array of Facebook action types
 * @returns Number of shares extracted from actions
 */
const extractSharesFromActions = (actions: FacebookActionType[]): number => {
    if (!actions || !Array.isArray(actions)) {
        return 0;
    }
    
    const sharesAction = actions.find(a => a.action_type === 'post');
    return sharesAction ? parseInt(String(sharesAction.value)) || 0 : 0;
};

/**
 * Helper function to get the display name for a metric
 * @param metric - The metric key
 * @returns Formatted display name
 */
const getMetricDisplayName = (metric: string): string => {
    const metricNames: Record<string, string> = {
        'clicks': 'Clicks',
        'impressions': 'Impressions', 
        'spend': 'Spent',
        'reach': 'Reach',
        'cpc': 'CPC',
        'cpm': 'CPM',
        'totalConversions': 'Conversions',
        'costPerConversion': 'Cost per Conversion',
        'likes': 'Likes',
        'comments': 'Comments', 
        'shares': 'Shares',
        'inline_link_clicks': 'Inline Link Clicks',
        'cost_per_inline_link_click': 'Cost per Inline Link Click'
    };
    
    return metricNames[metric] || metric;
};

/**
 * Helper function to get cost per metric display
 * @param metric - The metric key
 * @param costValue - The cost value
 * @param currency - Currency code
 * @returns Formatted cost per metric string
 */
const getCostPerMetricDisplay = (metric: string, costValue: number, currency?: CurrencyCodesType): string => {
    const costLabels: Record<string, string> = {
        'clicks': 'CPC',
        'impressions': 'CPM',
        'totalConversions': 'CPC', // Cost per conversion 
        'likes': 'CPL', // Cost per like
        'comments': 'CPC', // Cost per comment
        'shares': 'CPS', // Cost per share
        'inline_link_clicks': 'CPILC' // Cost per inline link click
    };
    
    const costLabel = costLabels[metric];
    if (!costLabel) return '';
    
    const formattedCost = formatNumberToDisplayType(costValue, 'currency', currency ?? DEFAULT_CURRENCY);
    
    return `${formattedCost} ${costLabel}`;
};

/** Valid keys for geographic region types */
type GeographicLocationType = 'regions' | 'countries' | 'dmas';

/** Mapping between geographic stats key and display name */
const GeographicAreaTypeNames: Record<GeographicLocationType, string> = {
    regions: 'Region',
    countries: 'Country',
    dmas: 'City'
}

/** Keys for campaign analytics stats */
type CampaignAnalyticsStatKey = 'clicks' | 'impressions';

/** Keys for sorting geographic breakdowns */
type GeographicSortMetricKey = 'clicks' | 'impressions' | 'spend' | 'reach' | 'cpc' | 'cpm' | 'totalConversions' | 'costPerConversion' | 'likes' | 'comments' | 'shares' | 'inline_link_clicks' | 'cost_per_inline_link_click';

/** Keys for sorting demographic breakdowns */
type DemographicSortMetricKey = 'clicks' | 'impressions' | 'spend' | 'cpc' | 'totalConversions' | 'costPerConversion' | 'likes' | 'comments' | 'shares' | 'inline_link_clicks' | 'cost_per_inline_link_click';

/** Mapping between campaign analytics key and display name */
const CampaignAnalyticsStatNames: Record<CampaignAnalyticsStatKey, string> = {
    clicks: 'Clicks',
    impressions: 'Impressions'
}

/** Sort options for geographic data */
const GEOGRAPHIC_SORT_OPTIONS: SelectOptionItem[] = [
    {
        value: 'clicks',
        title: 'Sort by Clicks',
        description: 'Show highest clicks first'
    },
    {
        value: 'impressions', 
        title: 'Sort by Impressions',
        description: 'Show highest impressions first'
    },
    {
        value: 'spend',
        title: 'Sort by Spent',
        description: 'Show highest spent first'
    },
    {
        value: 'reach',
        title: 'Sort by Reach',
        description: 'Show highest reach first'
    },
    {
        value: 'totalConversions',
        title: 'Sort by Conversions',
        description: 'Show highest conversions first'
    },
    {
        value: 'likes',
        title: 'Sort by Likes',
        description: 'Show highest likes first'
    },
    {
        value: 'comments',
        title: 'Sort by Comments',
        description: 'Show highest comments first'
    },
    {
        value: 'shares',
        title: 'Sort by Shares',
        description: 'Show highest shares first'
    },
    {
        value: 'cpc',
        title: 'Sort by Cost-per-click',
        description: 'Show lowest cost-per-click first'
    },
    {
        value: 'cpm',
        title: 'Sort by CPM',
        description: 'Show lowest CPM first'
    },
    {
        value: 'costPerConversion',
        title: 'Sort by Cost-per-conversion',
        description: 'Show lowest cost-per-conversion first'
    },
    {
        value: 'inline_link_clicks',
        title: 'Sort by Inline Link Clicks',
        description: 'Show highest inline link clicks first'
    }
]

/** Sort options for demographic data (no CPM, no reach) */
const DEMOGRAPHIC_SORT_OPTIONS: SelectOptionItem[] = [
    {
        value: 'clicks',
        title: 'Sort by Clicks',
        description: 'Show highest clicks first'
    },
    {
        value: 'impressions', 
        title: 'Sort by Impressions',
        description: 'Show highest impressions first'
    },
    {
        value: 'spend',
        title: 'Sort by Spent',
        description: 'Show highest spent first'
    },
    {
        value: 'totalConversions',
        title: 'Sort by Conversions',
        description: 'Show highest conversions first'
    },
    {
        value: 'likes',
        title: 'Sort by Likes',
        description: 'Show highest likes first'
    },
    {
        value: 'comments',
        title: 'Sort by Comments',
        description: 'Show highest comments first'
    },
    {
        value: 'shares',
        title: 'Sort by Shares',
        description: 'Show highest shares first'
    },
    {
        value: 'cpc',
        title: 'Sort by Cost-per-click',
        description: 'Show lowest cost-per-click first'
    },
    {
        value: 'costPerConversion',
        title: 'Sort by Cost-per-conversion',
        description: 'Show lowest cost-per-conversion first'
    },
    {
        value: 'inline_link_clicks',
        title: 'Sort by Inline Link Clicks',
        description: 'Show highest inline link clicks first'
    }
]

interface DemographicsStatsProps {
    className: string,
    style?: CSSProperties,
    currency?: CurrencyCodesType,
    demographicsBreakdown: CampaignDetailsData['demographicsBreakdown'],
    geographicBreakdown: CampaignDetailsData['geographicBreakdown'],
    isLoadingDemographics: boolean,
    showMarkets?: () => void,
    isAdBoxUnlocked?: boolean,
    onUnlockAdBox?: () => void,
}

const DemographicsStats = ({
    className,
    style,
    currency,
    demographicsBreakdown,
    geographicBreakdown,
    isLoadingDemographics,
    showMarkets,
    isAdBoxUnlocked = false,
    onUnlockAdBox,
}: DemographicsStatsProps) => {
    const { campaign } = useCampaignDetails();
    const { isProBrand, currentBrand } = useBrand()
    const { mobileView, mStyle } = useScreen()
    const { isAvailable: hasAdvancedAnalytics, minRequiredPlan, availablePlans } = useFeature(FeatureKeys.ANALYTICS_ADVANCED);

    const maxBreakdownItems: number = 5;

    // Primary stat key used to bucket the demographic and geographic analytics
    const primaryStatKey: CampaignAnalyticsStatKey = (campaign?.campaign_metadata?.playbook !== PlaybookType.TIKTOK_BOOST_ENGAGEMENT) ? 'clicks' : 'impressions';

    // Primary stat key display name
    const primaryStatName: string = CampaignAnalyticsStatNames[primaryStatKey].toLocaleLowerCase();
    const primaryStatNameSingular = primaryStatName.replace(/s$/, '')

    // Check if data should be shown
    const shouldShowData = hasAdvancedAnalytics || isAdBoxUnlocked;
    const isAdBoxCampaign = (campaign?.campaign_metadata as any)?.adbox_checkout === true && !hasAdvancedAnalytics;

    // Sort state management
    const [demographicsSortMetric, setDemographicsSortMetric] = useState<DemographicSortMetricKey>(primaryStatKey);
    const [geographicSortMetric, setGeographicSortMetric] = useState<GeographicSortMetricKey>(primaryStatKey);

    // Styles for the component
    const sty: Record<string, CSSProperties> = {
        sortControls: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: 16,
            gap: 8
        },
        sortSelect: {
            minWidth: mobileView ? 160 : 200
        }
    };

    /**
     * Helper function to sort breakdown data by selected metric
     * For cost-based metrics (cpc, cpm, costPerConversion), sorts ascending (lower is better)
     * For performance metrics (clicks, impressions, etc.), sorts descending (higher is better)
     */
    const sortBreakdownData = <T extends Record<string, any>>(
        data: T[], 
        sortMetric: string
    ): T[] => {
        const costMetrics = ['cpc', 'cpm', 'costPerConversion', 'cost_per_inline_link_click'];
        const isAscending = costMetrics.includes(sortMetric);
        
        return [...data].sort((a, b) => {
            const aValue = a[sortMetric] || 0;
            const bValue = b[sortMetric] || 0;
            
            if (isAscending) {
                return aValue - bValue; // Lower is better for costs
            }
            return bValue - aValue; // Higher is better for performance
        });
    };

    const renderLocationData = (locationType: GeographicLocationType) => {
        const locationTypeData = geographicBreakdown[locationType]

        if (!locationTypeData) {
            return null
        }

        const dataMappings: Record<string, any> = {}

        const filteredData = locationTypeData.filter((item: GeographicBreakdownItem) => {
            // Filter out Unknown DMAs
            if (item.dma !== null && item.dma === 'Unknown') {
                return false
            }
            // Filter out None countries
            if (item.country !== null && item.country === 'None') {
                return false
            }
            // Filter out items with Unknown country names (but keep regions which naturally have Unknown countryName)
            if (locationType === 'countries' && item.countryName === 'Unknown') {
                return false
            }
            return true
        })

        const mappedData = filteredData.map((item: GeographicBreakdownItem) => {
            let name
            let countryCode
            switch (locationType) {
                case 'regions':
                    name = item.region
                    countryCode = null
                    break;
                case 'countries':
                    name = item.countryName
                    countryCode = item.country
                    break
                case 'dmas':
                    name = item.dma
                    countryCode = null
                    break;
                default:
                    break;
            }

            if (name) {
                // Extract shares from actions if available, otherwise use direct property
                const extractedShares = item.actions ? extractSharesFromActions(item.actions) : item.shares;
                
                dataMappings[name] = {
                    type: locationType,
                    countryCode: countryCode ? countryCode : null,
                    clicks: item.clicks,
                    cpc: item.cpc,
                    reach: item.reach,
                    spend: item.spend,
                    impressions: item.impressions,
                    totalConversions: item.totalConversions,
                    costPerConversion: item.costPerConversion,
                    likes: item.likes,
                    comments: item.comments,
                    shares: extractedShares, // Use extracted shares
                    inline_link_clicks: item.inline_link_clicks,
                    cost_per_inline_link_click: item.cost_per_inline_link_click,
                    cpm: item.cpm
                }
            }

            // Extract shares from actions if available, otherwise use direct property
            const extractedShares = item.actions ? extractSharesFromActions(item.actions) : item.shares;
            
            const processedItem = {
                name: name,
                type: locationType,
                countryCode: countryCode ? countryCode : null,
                countryName: item.countryName ? item.countryName : null,
                clicks: item.clicks,
                cpc: item.cpc,
                reach: item.reach,
                spend: item.spend,
                impressions: item.impressions,
                totalConversions: item.totalConversions,
                costPerConversion: item.costPerConversion,
                likes: item.likes,
                comments: item.comments,
                shares: extractedShares, // Use extracted shares
                inline_link_clicks: item.inline_link_clicks,
                cost_per_inline_link_click: item.cost_per_inline_link_click,
                cpm: item.cpm
            };
            
            return {
                ...processedItem,
                value: processedItem[geographicSortMetric as keyof typeof processedItem] || 0, // Use selected sort metric
            }
        })

        const processedData = sortBreakdownData(mappedData, geographicSortMetric)
            .slice(0, maxBreakdownItems)

        const CustomizedAxisTick = ({
            x,
            y,
            payload
        }: any) => {
            const labelWidth = 120

            const item = dataMappings[payload.value]

            var itemName = item.type === 'countries' && mobileView ? item.countryCode : payload.value
            return (
                <g transform={`translate(${x - (labelWidth / 2)},${y}) ${mobileView ? `rotate(0 ${100 / 2} ${labelWidth / 2})` : ''}`}>
                    <foreignObject x={0} y={0} width={labelWidth} height={100} className="overflow-visible">
                        {mobileView ?
                            <div className="w-full text-center overflow-ellipses">
                                {item.countryCode && (<div >
                                    <ReactCountryFlag
                                        style={{
                                            fontSize: '0.9em'
                                        }}
                                        countryCode={item.countryCode} />
                                </div>)}
                                <div className="mt-0.5 text-sm" >{itemName}</div>
                            </div>
                            :
                            <div className="w-full text-center overflow-ellipses">
                                {item.countryCode && (<div >
                                    <ReactCountryFlag
                                        style={{
                                            fontSize: '1.1em'
                                        }}
                                        countryCode={item.countryCode} />
                                </div>)}
                                <div className="mt-0.5 text-base">{itemName}</div>
                                <div className="text-sm text-gray-500">
                                    {geographicSortMetric === 'spend' ? 
                                        formatNumberToDisplayType(item[geographicSortMetric], 'currency', currency ?? DEFAULT_CURRENCY) : 
                                        geographicSortMetric === 'cpm' ? 
                                        `${formatNumberToDisplayType(item[geographicSortMetric], 'currency', currency ?? DEFAULT_CURRENCY)} CPM` :
                                        geographicSortMetric === 'cpc' ? 
                                        `${formatNumberToDisplayType(item[geographicSortMetric], 'currency', currency ?? DEFAULT_CURRENCY)} CPC` :
                                        `${millify(item[geographicSortMetric])} ${getMetricDisplayName(geographicSortMetric)}`
                                    }
                                </div>
                                {(['clicks', 'totalConversions', 'likes', 'comments', 'shares', 'inline_link_clicks'].includes(geographicSortMetric)) &&
                                    <div className="text-sm text-gray-500">
                                        {getCostPerMetricDisplay(geographicSortMetric, item[geographicSortMetric === 'clicks' ? 'cpc' : geographicSortMetric === 'totalConversions' ? 'costPerConversion' : geographicSortMetric === 'inline_link_clicks' ? 'cost_per_inline_link_click' : 'cpc'], currency)}
                                    </div>
                                }
                            </div>
                        }
                    </foreignObject>
                </g>
            );
        }

        let barInterval: number | AxisInterval = 5
        if (mobileView) {
            if (locationType === 'countries') {
                barInterval = 0
            } else {
                barInterval = 1
            }
        } else {
            barInterval = 0
        }

        let emptyState = false
        if (processedData.length === 0) emptyState = true

        // Check if we have data for the selected metric
        const hasDataForMetric = processedData.some(item => {
            const value = item[geographicSortMetric as keyof typeof item];
            return value !== undefined && value !== null && Number(value) > 0;
        });

        let subtitleSentence: string
        
        if (!hasDataForMetric && !['clicks', 'impressions', 'spend', 'cpc'].includes(geographicSortMetric)) {
            const locationDisplayName = locationType === 'dmas' ? 'cities' : locationType;
            subtitleSentence = `No ${getMetricDisplayName(geographicSortMetric).toLowerCase()} data available for ${locationDisplayName}.`;
        } else if (processedData.length === 0) {
            subtitleSentence = `We're still waiting for data to come in for top ${locationType === 'dmas' ? 'cities' : locationType}.`;
        } else {
            const firstLocation = processedData[0] ? fixCountryNameForUnited(processedData[0].name!) : null;
            const secondLocation = processedData[1] ? fixCountryNameForUnited(processedData[1].name!) : null;
            
            if (secondLocation) {
                subtitleSentence = `Best-performing locations: ${firstLocation} and ${secondLocation}.`;
            } else if (firstLocation) {
                subtitleSentence = `Best-performing location: ${firstLocation}.`;
            } else {
                subtitleSentence = `We're still waiting for data to come in for top ${locationType === 'dmas' ? 'cities' : locationType}.`;
            }
        }

        return (
            <div className="demographicsStatsCustomBox" style={{ borderBottom: Sty.border, paddingTop: 24, paddingBottom: 24 }}>
                <div className="demographicsHeaderBoxInner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <CardSymphony
                        className="demographicsStatsHeader"
                        title={`Top ${GeographicAreaTypeNames[locationType]} by ${getMetricDisplayName(geographicSortMetric)}`}
                        subtitle={subtitleSentence}
                        isNoPadding
                        size="small"
                        style={{ marginBottom: 16 }}
                        isGemIcon
                    />
                    {showMarkets && !emptyState && !mobileView &&
                        <div className="flex-shrink-0">
                            <ButtonSymphony
                                className="buttonDemographicsStats"
                                onClick={showMarkets}
                                variant="text"
                            >
                                <BarChartOutlined /> See Stats
                            </ButtonSymphony>
                        </div>
                    }
                </div>
                {shouldShowData && (
                    <div className="geographicSortControls" style={sty.sortControls}>
                        <InputSymphony
                            className="geographicSortSelect"
                            isSelectDropdown={true}
                            style={mStyle(sty, 'sortSelect')}
                            options={GEOGRAPHIC_SORT_OPTIONS}
                            onOptionSelect={(option: any) => {
                                setGeographicSortMetric(option.value);
                            }}
                            value={geographicSortMetric}
                        />
                    </div>
                )}
                {!emptyState && hasDataForMetric && (
                    <div>
                        <ResponsiveContainer className="mx-auto" width="100%" height={window.innerWidth < 576 ? 300 : 300} minHeight="100px">
                            <BarChart
                                width={500}
                                height={300}
                                margin={{
                                    bottom: mobileView ? 20 : 80
                                }}
                                data={processedData}
                            >
                                <XAxis type="category" dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    interval={barInterval}
                                    tick={<CustomizedAxisTick />}
                                />
                                <YAxis
                                    hide={true}
                                    domain={[(dataMin: any) => (0), (dataMax: any) => (dataMax * 1.02)]}
                                />
                                <Tooltip
                                    cursor={{ fill: Colors.purple, fillOpacity: 0.1 }}
                                    content={({ active, payload, label }) => {

                                        if (active && payload && payload.length) {
                                            let mainData: any = payload[0]
                                            if (mainData && mainData.payload) {
                                                mainData = mainData.payload
                                            } else {
                                                return null
                                            }

                                            const cpc = formatNumberToDisplayType(mainData.cpc, 'currency', currency ?? DEFAULT_CURRENCY)

                                            const statsValue = geographicSortMetric === 'spend' ? 
                                                formatNumberToDisplayType(mainData[geographicSortMetric], 'currency', currency ?? DEFAULT_CURRENCY) :
                                                geographicSortMetric === 'cpm' || geographicSortMetric === 'cpc' || geographicSortMetric === 'costPerConversion' || geographicSortMetric === 'cost_per_inline_link_click' ?
                                                formatNumberToDisplayType(mainData[geographicSortMetric], 'currency', currency ?? DEFAULT_CURRENCY) :
                                                formatNumberToDisplayType(mainData[geographicSortMetric], 'number')

                                            const spend = formatNumberToDisplayType(mainData.spend, 'currency', currency ?? DEFAULT_CURRENCY)

                                            var mainLabel = label
                                            if (locationType === 'regions') {
                                                mainLabel = `${mainLabel}`
                                            }
                                            return (
                                                <GraphTooltip
                                                    title={mainLabel}
                                                    value={(
                                                        <div>
                                                            <p className="font-semibold text-md">
                                                                {statsValue} {getMetricDisplayName(geographicSortMetric)}</p>
                                                            {(['clicks', 'totalConversions', 'likes', 'comments', 'shares', 'inline_link_clicks'].includes(geographicSortMetric)) &&
                                                                <p className="font-semibold text-md">
                                                                    {getCostPerMetricDisplay(geographicSortMetric, mainData[geographicSortMetric === 'clicks' ? 'cpc' : geographicSortMetric === 'totalConversions' ? 'costPerConversion' : geographicSortMetric === 'inline_link_clicks' ? 'cost_per_inline_link_click' : 'cpc'], currency)}
                                                                </p>
                                                            }
                                                            <p className="font-semibold text-md">
                                                                {spend} spent
                                                            </p>
                                                        {mainData.conversions !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.conversions, 'number')} conversions</p>}
                                                        {mainData.cpm !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.cpm, 'currency', currency ?? DEFAULT_CURRENCY)} CPM</p>}
                                                        {mainData.actions !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.actions, 'number')} actions</p>}
                                                        {mainData.inline_link_clicks !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.inline_link_clicks, 'number')} inline link clicks</p>}
                                                        {mainData.outbound_clicks !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.outbound_clicks, 'number')} outbound clicks</p>}
                                                        {mainData.cost_per_action_type !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.cost_per_action_type, 'currency', currency ?? DEFAULT_CURRENCY)} cost per action type</p>}
                                                        {mainData.purchase_roas !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.purchase_roas, 'number')} purchase ROAS</p>}
                                                        {mainData.cost_per_conversion !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.cost_per_conversion, 'currency', currency ?? DEFAULT_CURRENCY)} cost per conversion</p>}
                                                        {mainData.cost_per_inline_link_click !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.cost_per_inline_link_click, 'currency', currency ?? DEFAULT_CURRENCY)} cost per inline link click</p>}
                                                        </div>
                                                    )}
                                                />
                                            );
                                        }

                                        return null;
                                    }}

                                // formatter={(value: any, name: any, props: any) => [value.toLocaleString(), "Total Fanbase Size"]} 
                                />

                                <Bar
                                    dataKey="value"
                                    fill="#C698FF"
                                    radius={6}
                                >
                                    {/* <LabelList dataKey="value" position="top"
                                    formatter={(value: number) => `${value.toLocaleString('en', {
                                        maximumFractionDigits: 0,
                                    })}%`}
                                    style={{ fill: "black" }} /> */}
                                </Bar>

                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        )
    }

    const renderDemographicsData = () => {
        if (!demographicsBreakdown) {
            return null
        }

        const dataMappings: Record<string, any> = {}

        // Filter out Unknown age/gender combinations and entries with zero/invalid data
        const filteredDemographicsData = demographicsBreakdown.filter((item: DemographicsBreakdownItem) => {
            // Filter out Unknown age or gender
            if (item.age === 'Unknown' || item.gender === 'unknown') {
                return false
            }
            // Filter out entries with no meaningful data (0 clicks, impressions, spend)
            if (item.clicks === 0 && item.impressions === 0 && item.spend === 0) {
                return false
            }
            return true
        })

        const mappedDemographicsData = filteredDemographicsData
            .map((item: DemographicsBreakdownItem) => {
                let capitalizedGender = capitalizeFirstLetter(item.gender)
                const dataLabel = `Age ${item.age} / ${capitalizedGender}`
                
                // Extract shares from actions if available, otherwise use direct property
                const extractedShares = item.actions ? extractSharesFromActions(item.actions) : item.shares;

                const processedItem = {
                    age: item.age,
                    gender: capitalizedGender,
                    name: dataLabel,
                    clicks: item.clicks,
                    cpc: item.cpc,
                    spend: item.spend,
                    impressions: item.impressions,
                    totalConversions: item.totalConversions,
                    costPerConversion: item.costPerConversion,
                    likes: item.likes,
                    comments: item.comments,
                    shares: extractedShares, // Use extracted shares
                    inline_link_clicks: item.inline_link_clicks,
                    cost_per_inline_link_click: item.cost_per_inline_link_click
                };

                dataMappings[dataLabel] = {
                    ...processedItem,
                    value: processedItem[demographicsSortMetric as keyof typeof processedItem] || 0,
                }

                return {
                    ...processedItem,
                    value: processedItem[demographicsSortMetric as keyof typeof processedItem] || 0,
                }
            })

        const formattedDemographicsData = sortBreakdownData(mappedDemographicsData, demographicsSortMetric)
            .slice(0, maxBreakdownItems)

        const CustomizedDemoAxisTick = ({
            x,
            y,
            height,
            payload
        }: any) => {
            const yAxisTitle = dataMappings[payload.value]

            return (
                <text
                    x={x}
                    y={y}
                    height={height}
                    className="rechatsPersonalClassIdentifier recharts-text recharts-layer recharts-cartesian-axis-tick-value"
                    type="category"
                    orientation="left"
                    textAnchor="end"
                    style={{
                        fontSize: '0.95rem'
                    }}>
                    <tspan x={x} dy="-0.145em">{yAxisTitle.age}</tspan>
                    <tspan x={x} dy="1.3em">{yAxisTitle.gender}</tspan>
                </text>

            );
        }

        const renderCustomizedLabel = (labelProps: any) => {
            const {
                height,
                value,
                offset,
                index
            } = labelProps;

            const yValue = (index * 60) + 30

            // Get the container width for right alignment
            const containerWidth = document.querySelector('.recharts-wrapper')?.getBoundingClientRect().width || 0
            const rightPadding = 20 // Padding from right edge

            const formattedValue = demographicsSortMetric === 'spend' ? 
                formatNumberToDisplayType(value, 'currency', currency ?? DEFAULT_CURRENCY) :
                demographicsSortMetric === 'cpc' || demographicsSortMetric === 'costPerConversion' || demographicsSortMetric === 'cost_per_inline_link_click' ?
                formatNumberToDisplayType(value, 'currency', currency ?? DEFAULT_CURRENCY) :
                millify(value);

            if (mobileView) {
                return (
                    <text
                        y={yValue}
                        height={height}
                        offset={offset}
                        style={{
                            fontSize: '0.8rem',
                            fill: '#000'
                        }}
                    >
                        <tspan
                            x={containerWidth - rightPadding}
                            dy="-0.145em"
                            textAnchor="end"
                        >
                            {formattedValue}
                        </tspan>
                        <tspan
                            x={containerWidth - rightPadding}
                            dy="1.2em"
                            textAnchor="end"
                        >
                            {getMetricDisplayName(demographicsSortMetric)}
                        </tspan>
                    </text>
                )
            }

            return (
                <text
                    x={containerWidth - rightPadding}
                    y={yValue}
                    height={height}
                    style={{
                        fontSize: '0.9rem',
                        fill: '#000'
                    }}
                    textAnchor="end"
                    dominantBaseline="middle"
                >
                    <tspan>
                        {formattedValue} {getMetricDisplayName(demographicsSortMetric)}
                    </tspan>
                </text>
            )
        };

        let emptyState = false
        if (formattedDemographicsData.length < 2) {
            emptyState = true
        }

        // Check if we have data for the selected metric
        const hasDataForMetric = formattedDemographicsData.some(item => {
            const value = item[demographicsSortMetric as keyof typeof item];
            return value !== undefined && value !== null && Number(value) > 0;
        });

        let subtitleSentence: string

        if (!hasDataForMetric && !['clicks', 'impressions', 'spend', 'cpc'].includes(demographicsSortMetric)) {
            subtitleSentence = `No ${getMetricDisplayName(demographicsSortMetric).toLowerCase()} data available for demographics.`;
        } else if (emptyState) {
            subtitleSentence = "We're still waiting for data to come in.";
        } else {
            const firstDemo = formattedDemographicsData[0];
            const secondDemo = formattedDemographicsData[1];
            
            if (secondDemo) {
                subtitleSentence = `Best-performing demographics: ${firstDemo.gender}s aged ${firstDemo.age} and ${secondDemo.gender}s aged ${secondDemo.age}.`;
            } else if (firstDemo) {
                subtitleSentence = `Best-performing demographic: ${firstDemo.gender}s aged ${firstDemo.age}.`;
            } else {
                subtitleSentence = "We're still waiting for data to come in.";
            }
        }

        const graphHeight = formattedDemographicsData.length * 60;

        return (
            <div className="topDemographicsContainer border-b py-7">
                <CardSymphony
                    className="topDemographicsHeader"
                    title={`Top Demographics by ${getMetricDisplayName(demographicsSortMetric)}`}
                    subtitle={subtitleSentence}
                    isNoPadding
                    size="small"
                    style={{ marginBottom: 16 }}
                />
                {shouldShowData && (
                    <div className="demographicsSortControls" style={sty.sortControls}>
                        <InputSymphony
                            className="demographicsSortSelect"
                            isSelectDropdown={true}
                            style={mStyle(sty, 'sortSelect')}
                            options={DEMOGRAPHIC_SORT_OPTIONS}
                            onOptionSelect={(option: any) => {
                                setDemographicsSortMetric(option.value);
                            }}
                            value={demographicsSortMetric}
                        />
                    </div>
                )}
                {!emptyState && hasDataForMetric &&
                    <ResponsiveContainer
                        className="mx-auto"
                        width="100%"
                        height={graphHeight}
                        minHeight="300px"
                    >
                        <BarChart
                            id="barChart"
                            width={500}
                            height={graphHeight}
                            // margin={{
                            //     bottom: 80
                            // }}
                            margin={{
                                left: mobileView ? 0 : 20
                            }}
                            layout="vertical"
                            data={formattedDemographicsData}
                        >
                            <XAxis
                                type="number"
                                hide
                                domain={[(dataMin: any) => (0), (dataMax: any) => (dataMax * 1.3)]}
                            />

                            <YAxis
                                type="category"
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                minTickGap={0}
                                tickMargin={10}
                                tick={<CustomizedDemoAxisTick />}
                            />

                            <Tooltip
                                cursor={{ fill: Colors.purple, fillOpacity: 0.1 }}
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        let mainData: any = payload[0]
                                        if (mainData && mainData.payload) {
                                            mainData = mainData.payload
                                        } else {
                                            return null
                                        }

                                        const cpc = formatNumberToDisplayType(mainData.cpc, 'currency', currency ?? DEFAULT_CURRENCY)

                                        const primaryStatValue = demographicsSortMetric === 'spend' ? 
                                            formatNumberToDisplayType(mainData[demographicsSortMetric], 'currency', currency ?? DEFAULT_CURRENCY) :
                                            demographicsSortMetric === 'cpc' || demographicsSortMetric === 'costPerConversion' || demographicsSortMetric === 'cost_per_inline_link_click' ?
                                            formatNumberToDisplayType(mainData[demographicsSortMetric], 'currency', currency ?? DEFAULT_CURRENCY) :
                                            formatNumberToDisplayType(mainData[demographicsSortMetric], 'number')

                                        const spend = formatNumberToDisplayType(mainData.spend, 'currency', currency ?? DEFAULT_CURRENCY)

                                        var mainLabel = mainData.name

                                        return (
                                            <GraphTooltip
                                                title={mainLabel}
                                                value={(
                                                    <div className="demographicsTooltip">
                                                        <p className="font-semibold text-md">
                                                            {primaryStatValue} {getMetricDisplayName(demographicsSortMetric)}</p>
                                                        {(['clicks', 'totalConversions', 'likes', 'comments', 'shares', 'inline_link_clicks'].includes(demographicsSortMetric)) &&
                                                            <p className="font-semibold text-md">
                                                                {getCostPerMetricDisplay(demographicsSortMetric, mainData[demographicsSortMetric === 'clicks' ? 'cpc' : demographicsSortMetric === 'totalConversions' ? 'costPerConversion' : demographicsSortMetric === 'inline_link_clicks' ? 'cost_per_inline_link_click' : 'cpc'], currency)}
                                                            </p>
                                                        }
                                                        <p className="font-semibold text-md">
                                                            {spend} spent
                                                        </p>
                                                        {mainData.conversions !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.conversions, 'number')} conversions</p>}
                                                        {mainData.cpm !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.cpm, 'currency', currency ?? DEFAULT_CURRENCY)} CPM</p>}
                                                        {mainData.actions !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.actions, 'number')} actions</p>}
                                                        {mainData.inline_link_clicks !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.inline_link_clicks, 'number')} inline link clicks</p>}
                                                        {mainData.outbound_clicks !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.outbound_clicks, 'number')} outbound clicks</p>}
                                                        {mainData.cost_per_action_type !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.cost_per_action_type, 'currency', currency ?? DEFAULT_CURRENCY)} cost per action type</p>}
                                                        {mainData.purchase_roas !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.purchase_roas, 'number')} purchase ROAS</p>}
                                                        {mainData.cost_per_conversion !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.cost_per_conversion, 'currency', currency ?? DEFAULT_CURRENCY)} cost per conversion</p>}
                                                        {mainData.cost_per_inline_link_click !== undefined && <p className="font-semibold text-md">{formatNumberToDisplayType(mainData.cost_per_inline_link_click, 'currency', currency ?? DEFAULT_CURRENCY)} cost per inline link click</p>}
                                                    </div>
                                                )}
                                            />
                                        );
                                    }

                                    return null;
                                }}
                            // formatter={(value: any, name: any, props: any) => [value.toLocaleString(), "Total Fanbase Size"]} 
                            />

                            <Bar
                                dataKey="value"
                                id="bar"
                                radius={6}
                                fill="#D3B7F7"
                                background={{ fill: '#EDECF2', radius: 6 }}
                                barSize={50}
                            >
                                <LabelList
                                    dataKey="value"
                                    position="right"
                                    formatter={(value: number) => {
                                        const formattedValue = demographicsSortMetric === 'spend' ? 
                                            formatNumberToDisplayType(value, 'currency', currency ?? DEFAULT_CURRENCY) :
                                            demographicsSortMetric === 'cpc' || demographicsSortMetric === 'costPerConversion' || demographicsSortMetric === 'cost_per_inline_link_click' ?
                                            formatNumberToDisplayType(value, 'currency', currency ?? DEFAULT_CURRENCY) :
                                            formatNumberToDisplayType(value, 'number');
                                        return `${formattedValue} ${getMetricDisplayName(demographicsSortMetric)}`;
                                    }}
                                    style={{ fill: "black" }}
                                    content={renderCustomizedLabel}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                }
            </div>
        )
    }

    if (isLoadingDemographics || !(geographicBreakdown && demographicsBreakdown)) {
        return (
            <div
                className="demographicsStatsLoading"
                style={{
                    margin: '24px auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <Loader
                    className="demographicsStatsLoader"
                    size={44}
                    title="Loading Demographics..."
                />
            </div>
        );
    }

    if (!shouldShowData) {
        return (
            <>
                <div className="notProBrandContentContainer px-6">
                    <CardSymphony
                        className="topDemographicsHeader"
                        title="Top Countries by Clicks"
                        subtitle={`These are the top ${maxBreakdownItems} countries that clicked on your ads the most.`}
                        isNoPadding
                        size="small"
                        isGemIcon
                    />
                    <BlurredGraph
                        className="notProBrandImage"
                        graph="topCountries"
                        title="Track your ad analytics by country"
                        buttonText={isAdBoxCampaign ? "Unlock for $20" : "Upgrade to Growth"}
                        upgradePlan={isAdBoxCampaign ? PlanType.PRO : (minRequiredPlan || PlanType.GROWTH)}
                        onClick={isAdBoxCampaign ? onUnlockAdBox : undefined}
                    />
                    <CardSymphony
                        className="topDemographicsHeader"
                        title="Top Demographics by Clicks"
                        subtitle={`These are the top ${maxBreakdownItems} age & gender breakdowns that clicked on your ads the most.`}
                        isNoPadding
                        size="small"
                        isGemIcon
                    />
                    <BlurredGraph
                        className="notProBrandImage"
                        graph="topDemographics"
                        title="Track your ad analytics by age & gender"
                        buttonText={isAdBoxCampaign ? "Unlock for $20" : "Upgrade to Growth"}
                        upgradePlan={isAdBoxCampaign ? PlanType.PRO : (minRequiredPlan || PlanType.GROWTH)}
                        onClick={isAdBoxCampaign ? onUnlockAdBox : undefined}
                    />
                </div>
            </>
        );
    }

    return (
        <div
            className={clsx('demographicsStatsComponent', className)}
            style={styx(style)}
        >
            {renderDemographicsData()}
            {renderLocationData('countries')}
            {renderLocationData('regions')}
            {renderLocationData('dmas')}
        </div>
    )
}

export default DemographicsStats;